import request from './request'
import { QueryPageDto, QueryDateSlotDto } from './dto'

interface ImportAddressBookDto {
  file: File
}

export interface PaginationDto<T> {
  hasNextPage: boolean
  list: T[]
  totalCount: number
}

export interface ImportAddressBookResult {
  workerName: string
  idCardNo: string
  phone: string
  errorMsg: string
}

// 导入简历库名单
export function importAddressBook(data: ImportAddressBookDto): Promise<ImportAddressBookResult[]> {
  const formData = new FormData()
  formData.append('file', data.file)
  return request.post('/rest/pay/address-book/importAddressBook', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
}

// 通用图片上传
export function apiUploadFile(data: { file: string, filePath: string }): Promise<any> {
  const formData = new FormData()
  formData.append('file', data.file)
  formData.append('filePath', 'file/img/')
  return request.post('/upload-file', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
}

export interface Label {
  id: number
  companyId: number
  labelName: string
  labelType: number
  createTime: string
  updateTime: string
  delFlag: number
  updateUserId: null
  createUserId: string
  jobPreferences: string
  labelCategory: number
  labelDescription: string
  params: {}
}

interface GetLabelResult {
  systemLabel: Label[]
  customLabel: Label[]
}

// 查询标签
export function getLabel(): Promise<GetLabelResult> {
  return request.get('/rest/saas/label/getLabel')
}

// 添加自定义标签
export function addLabel(data: { labelName: string }): Promise<boolean> {
  return request.post('/rest/saas/label/addLabel', data)
}

interface DeleteLabelDto {
  id: number
}

// 删除自定义标签
export function deleteLabel(data: DeleteLabelDto): Promise<void> {
  return request.post('/rest/saas/label/deleteLabel', data)
}

interface AddAndRemoveUserLabelDto {
  addressBookIds: number[]
  labelIds: number[]
}

// 批量添加员工标签
export function addUserLabel(data: AddAndRemoveUserLabelDto): Promise<void> {
  return request.post('/rest/saas/user/label/addUserLabel', data)
}

export interface removeUserLabelDto {
  deleteCount: number
  errorList: errorList[]
}

interface errorList {
  addressBookId: number
  reason: string
  workerName: string
}

// 批量移除标签
export function removeUserLabel(data: AddAndRemoveUserLabelDto): Promise<removeUserLabelDto> {
  return request.post('/rest/saas/user/label/removeUserLabel', data)
}

interface RevokeResignLabelDto {
  addressBookIds: number[]
}

// 批量撤销离职
export function revokeResignLabel(data: RevokeResignLabelDto): Promise<void> {
  return request.post('/rest/saas/user/label/revokeResignLabel', data)
}

interface JobIntention {
  workLocationList: string[]
  longTermWorkList: string[]
  workPeriodList: string[]
  intentionIndustryList: string[]
}

interface EducationExperience {
  schoolName: null
  educationLevel: null
  startDate: null
  endDate: null
}

interface UserLabel {
  id: number
  companyId: number
  labelName: string
  labelType: number
  jobPreferences: null
  labelCategory: number
  createUserId: null
  createTime: string
  updateTime: string
  delFlag: number
  updateUserId: null
  params: object
}

export interface QueryUserDetailInfoResult {
  addressBookId: number
  userId: number
  userHeadUrl: string
  userName: string
  sex: string
  age: number
  phone: string
  idCardNo: string
  bankCardNo: string
  bankCardImg: string
  healthCard: string
  jobIntention: JobIntention
  labelList: UserLabel[]
  area: string
  educationExperiences: EducationExperience[]
  idCardBack: string
  idCardFront: string
  nickName: string
}

// 查询员工基本信息
export function queryUserDetailInfo(data: { userId: number }): Promise<QueryUserDetailInfoResult> {
  return request.get('/rest/pay/address-book/queryUserDatailInfo', {
    params: data
  })
}

// 查询员工工作记录
export function queryUserWorkRecord(data: { userId: number, queryMonth: string }): Promise<PaginationDto<QueryEmployeeSalaryBillResult>> {
  return request.get('/rest/pay/address-book/queryUserWorkRecord', {
    params: data
  })
}

interface QueryEmployeeListDto {
  pageNum?: string
  pageSize?: string
  searchText?: string
  statusFilter?: string // 状态筛选（inactive-待激活，insured-保障中，pending-待生效）
  sex?: string
  isLogout?: string
  uncontacted?: boolean
  labelIds?: number[]
  dateFilter?: string
  isResign?: string
  startDate?: string
  endDate?: string
  latestProjectName?: string
  serveCompanyName?: string
  phone?: string
  idCardNo?: string
  idCardNos?: string
}

interface Employee {
  createTime: string
  hasCalled: boolean
  id: number
  idCardLast4: string
  isLogout: number
  isResign: number
  labels: UserLabelDto[]
  phone: string
  sex: string
  userHeadUrl: string
  userId: number
  workerName: string
  age: number
  bankCardImg: string
  bankCardNo: string
  idCardBack: string
  idCardFront: string
  idCardNo: string
  latestProjectName: string
  projectRemark: string
  serveCompanyName: string
}

interface QueryEmployeeListResult {
  hasNextPage: boolean
  list: Employee[]
  totalCount: number
  femaleCount: number
  maleCount: number
}

export type { Employee, QueryEmployeeListResult }

interface UserLabelDto {
  addressBookId: number
  labelId: number
  labelName: string
}

// 查询简历库列表
export function queryEmployeeList(data: QueryEmployeeListDto): Promise<QueryEmployeeListResult> {
  return request.get('/rest/pay/address-book/employee-list', {
    params: data
  })
}

interface QueryEmployeeSalaryBillDto {
  /**
     * 简历库id
     */
  id?: string;
  /**
   * 查询月份
   */
  queryMonth?: string;
}

export interface QueryEmployeeSalaryBillResult {
  /**
      * 发薪金额
      */
  paymentAmount: string;
  /**
   * 发薪时间
   */
  payTime: string;
  /**
   * 项目名称
   */
  projectName: string;
  /**
   * 用工企业
   */
  serveCompanyName: string;
  /**
   * 是否打卡，无打卡-0；需打卡-1
   */
  isClockIn: number;
  /**
   * 1长期工，日结工0
   */
  type: number;
  /**
   * 是否领薪，未领取-0；已领取-1，2=停止发薪，3-进行中，4-领薪失败
   */
  status?: number;
}

// 查询员工发薪记录
export function queryEmployeeSalaryBill(data: QueryEmployeeSalaryBillDto): Promise<PaginationDto<QueryEmployeeSalaryBillResult>> {
  return request.get('/rest/pay/address-book/getEmployeeSalaryBill', {
    params: data
  })
}
export interface ExportEmployeeListResult {
  id: number
  companyId: number
  exportType: number
  exportStatus: number
  downloadStatus: number
  returnClientStatus: number
  fileType: number
  sysType: number
  fileName: string
  fileUrl: string
  exportUrl: string
  queryCondition: string
  params: Record<string, any>
  remark: string
  title: string
  creator: string
  createUserId: number
  createTime: number
  createFileTime: number
  updateTime: number
  downloadTime: number
}

// 导出表格
export function exportEmployeeList(data: QueryEmployeeListDto): Promise<ExportEmployeeListResult> {
  return request.get('/pay-module-export/address-book/employee-list/export', {
    params: data,
    headers: { loading: 'skip' }
  })
}
